// The views of property results (generic per view type): curves, bands (fat bands by weight, ⟨σz⟩ colours,
// comparison overlays), k-plane maps and tables; with the summary, warnings and notes.
import { Alert, Group, SegmentedControl, Select, Stack, Table, Tabs, Text } from "@mantine/core";
import { useMemo, useState } from "react";
import { Plot } from "../../components/Plot";
import { bandsFigure, curveFigure, engineTitle, mapFigure, type PropertyResult, type View } from "../../lib/properties";

type BandsView = Extract<View, { type: "bands" }>;

function BandsPlot({ views }: { views: BandsView[] }) {
  const groups = Object.keys(views[0].weights ?? {});
  const spin = !!views[0].spin_z;
  const [mode, setMode] = useState<string>("plain");
  const [group, setGroup] = useState<string | null>(groups[0] ?? null);
  const fig = useMemo(() => bandsFigure(views, mode === "weights" && group ? { group } : mode === "spin" ? { spin: true } : undefined),
    [views, mode, group]);
  const modes = [{ value: "plain", label: "Bands" }, ...(groups.length ? [{ value: "weights", label: "Weights" }] : []),
    ...(spin ? [{ value: "spin", label: "⟨σz⟩" }] : [])];
  return (
    <Stack gap={4}>
      {views.length === 1 && modes.length > 1 && (
        <Group gap="xs">
          <SegmentedControl size="xs" value={mode} onChange={setMode} data={modes} />
          {mode === "weights" && <Select size="xs" w={160} data={groups} value={group} onChange={setGroup} allowDeselect={false} />}
        </Group>
      )}
      <Plot data={fig.data} layout={fig.layout} height={420} />
    </Stack>
  );
}

function ViewPanel({ v }: { v: View }) {
  const fig = useMemo(() => (v.type === "curve" ? curveFigure(v) : v.type === "map" ? mapFigure(v) : null), [v]);
  if (v.type === "bands") return <BandsPlot views={[v]} />;
  if (v.type === "table")
    return (
      <Table fz="xs" withTableBorder striped data-testid="result-table">
        <Table.Thead><Table.Tr>{v.headers.map((h) => <Table.Th key={h}>{h}</Table.Th>)}</Table.Tr></Table.Thead>
        <Table.Tbody>{v.rows.map((r, i) => <Table.Tr key={i}>{r.map((c, j) => <Table.Td key={j}>{String(c)}</Table.Td>)}</Table.Tr>)}</Table.Tbody>
      </Table>
    );
  return fig ? <Plot data={fig.data} layout={fig.layout} height={v.type === "map" ? 480 : 400} /> : null;
}

/** The views of one result: overlay bands views (model comparison) are drawn together. */
export function groupViews(views: View[]): { title: string; views: View[] }[] {
  const overlay = views.filter((v) => v.type === "bands" && v.overlay) as BandsView[];
  const rest = views.filter((v) => !(v.type === "bands" && v.overlay));
  return [...(overlay.length ? [{ title: "Bands", views: overlay as View[] }] : []), ...rest.map((v) => ({ title: v.title, views: [v] }))];
}

export function PropertyResultView({ r }: { r: PropertyResult }) {
  const groups = groupViews(r.views);
  return (
    <Stack gap="xs" data-testid="property-result">
      {r.warnings.map((w) => <Alert key={w} color="yellow" p="xs"><Text size="xs">{w}</Text></Alert>)}
      {groups.length === 1 ? <Body g={groups[0]} /> : (
        <Tabs defaultValue="0">
          <Tabs.List>{groups.map((g, i) => <Tabs.Tab key={i} value={String(i)}>{short(g.title)}</Tabs.Tab>)}</Tabs.List>
          {groups.map((g, i) => <Tabs.Panel key={i} value={String(i)} pt="xs"><Body g={g} /></Tabs.Panel>)}
        </Tabs>
      )}
      {r.summary.length > 0 && (
        <Table fz="xs" withTableBorder data-testid="result-summary">
          <Table.Tbody>{r.summary.map(([a, b]) => <Table.Tr key={a}><Table.Td>{a}</Table.Td><Table.Td>{b}</Table.Td></Table.Tr>)}</Table.Tbody>
        </Table>
      )}
      {r.notes.map((n) => <Text key={n} size="xs" c="dimmed">{n}</Text>)}
      <Text size="xs" c="dimmed">{r.calculator.title} v{r.calculator.version} · {engineTitle(r.engine)} engine{versionOf(r)} · {r.seconds} s ·
        {" "}{String(r.model.orbitals)} orbitals{r.model.spinful ? " (spinful)" : ""}</Text>
    </Stack>
  );
}

function Body({ g }: { g: { views: View[] } }) {
  if (g.views.length > 1) return <BandsPlot views={g.views as BandsView[]} />;
  return <ViewPanel v={g.views[0]} />;
}

const versionOf = (r: PropertyResult) => {
  const v = r.engine !== "native" ? r.versions?.[r.engine] : "";
  return v ? ` ${v}` : "";
};

const short = (t: string) => (t.length > 40 ? `${t.slice(0, 38)}…` : t);
